<template>
  <div class="admin-home-page">
    <div class="page-title">
      <div class="page-title-text-area">
        <h1>Admin</h1>
      </div>
    </div>
    <div class="stats">
      <div v-for="stat in stats" :key="stat.label" class="stat-card">
        <div class="stat-number">
          <LoadingIndicator v-if="stat.value === undefined"/>
          <template v-else>{{ stat.value?.toLocaleString() ?? '—' }}</template>
        </div>
        <div class="stat-label">{{ stat.label }}</div>
      </div>
    </div>
    <div class="dashboard">
      <router-link v-for="card in cards" :key="card.to" class="card" :to="card.to">
        <div class="card-title">{{ card.title }}</div>
        <div class="card-text">{{ card.text }}</div>
      </router-link>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.stats {
  margin-bottom: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.stat-card {
  padding: 1rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 200;
}

.stat-label {
  font-size: 0.875rem;
  color: #888;
}

.dashboard {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  }
}

.card {
  padding: 1rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  color: #333;
  text-decoration: none;

  &:hover {
    border-color: var(--bs-primary);
  }
}

.card-title {
  margin-bottom: 0.5rem;
}

.card-text {
  font-size: 0.875rem;
  color: #888;
}
</style>

<script setup lang="ts">
import { computed, onMounted, ref, Ref } from 'vue'
import { usePageTitle } from '@/util/AppUtil'
import { useUserStore } from '@/gp/UserStore'
import { useSyncStore } from '@/rd/SyncStore'
import LoadingIndicator from '@/util/LoadingIndicator.vue'

usePageTitle('Admin')

const userStore = useUserStore()
const syncStore = useSyncStore()

// undefined while loading, null if the metric isn't available for this user's privileges
const totalUsersRef = ref<number | null>()
const totalSyncsRef = ref<number | null>()

const stats = computed(() => [
  { label: 'Total users', value: totalUsersRef.value },
  { label: 'Total syncs', value: totalSyncsRef.value },
])

const cards = [
  { to: '/admin/users', title: 'Manage users', text: 'Search users and review sync, sharing, and plan activity.' },
  { to: '/admin/identities', title: 'Manage identities', text: 'Review and manage sign-in identities.' },
]

onMounted(() => {
  loadStat(() => userStore.getUserCountAsync(), totalUsersRef)
  loadStat(() => syncStore.getSyncCountAsync(), totalSyncsRef)
})

async function loadStat(fetch: () => Promise<number>, target: Ref<number | null | undefined>) {
  try {
    target.value = await fetch()
  }
  catch {
    target.value = null
  }
}
</script>
